import { useMsal } from "@azure/msal-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import type { AugmentedShipment } from "~/features/shipment-management/hooks/use-get-shipment.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { groupRemarksOrCheckCallsByDate } from "~/features/shipment-management/utils/group-remarks-by-date.ts";
import type { ShipmentRemark } from "~/shared/schemas/shipment-management-schema.ts";
import { newRemarkResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { patchAndValidate } from "~/shared/services/api/fetch-client.ts";

export const putRemark = async (messageData: {
	remark: ShipmentRemark;
	poNumber: string;
	message: string;
}) => {
	const url = apiRoutes.PUTDELETEREMARK.replace(
		":poNumber",
		messageData.poNumber,
	).replace(":remarkId", messageData.remark.id?.toString() ?? "");
	const resp = await patchAndValidate(
		url,
		messageData.message,
		newRemarkResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePutRemark = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { accounts } = useMsal();
	const currentUserName = accounts[0]?.name ?? null;

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (messageData: {
			remark: ShipmentRemark;
			poNumber: string;
			message: string;
		}) => putRemark(messageData),
		onSuccess: (savedRemark) => {
			const updatedRemark: ShipmentRemark = {
				id: savedRemark.remarkId,
				dateEntered: new Date().toISOString(),
				text: savedRemark.remarkText,
				contactInfo: {
					name: currentUserName,
					phone: null,
					extension: null,
					email: null,
				},
			};

			queryClient.setQueriesData<AugmentedShipment>(
				{ queryKey: ["shipment"] },
				(old) => {
					if (!old) {
						return old;
					}
					const remarks = (old.remarks ?? []).map((remark) =>
						remark.id === updatedRemark.id ? updatedRemark : remark,
					);
					return {
						...old,
						remarks,
						groupedRemarks: groupRemarksOrCheckCallsByDate(remarks),
					};
				},
			);
			showSuccess(SHIPMENT_TOAST_MESSAGES.remarkUpdated);
		},
		onError: () => {
			showError("Failed to update remark. Please try again.");
		},
	});

	return {
		putRemark: mutate,
		putRemarkAsync: mutateAsync,
		isPuttingRemark: isPending,
		error,
		data,
		resetError: reset,
	};
};
